/**
 * Where the helpStack chat launcher may sit (release walk B7, verify r4).
 *
 * The widget is a third-party script (layout.tsx loads helpstack.eu/widget.js)
 * whose corner and offset come from the helpStack dashboard (bottom corner,
 * 20 px). That is exactly where the shop's sticky bottom CTAs sit: the upload
 * confirm bar ("Potrdi in naloži"), the cart drawer's footer ("Nadaljuj na
 * blagajno"), the checkout step buttons ("Naprej") and the price bar.
 *
 * A fixed lift (an earlier 96 px) was wrong on every page, because each bar
 * has its own height. So every such bar carries `data-chat-avoid`, the page
 * MEASURES the ones actually on screen in the launcher's column, and the
 * launcher is placed `CHAT_GAP_PX` above the highest of them through the
 * `--chat-lift` CSS variable. When that would push it into the upper half of
 * the screen, it is hidden instead of overlapping anything.
 *
 * The widget's element ids are stable (#chat-widget-bubble, -branding,
 * -proactive, -grabber), so the CSS below moves them; a launcher the visitor
 * dragged carries an inline `top:` and is left where they put it.
 */

/** Gap between the launcher and the bar it sits above. */
export const CHAT_GAP_PX = 10;
const LAUNCHER_PX = 60;
/** The widget's own bottom offset (dashboard default). */
const BASE_OFFSET_PX = 20;
/** A bar counts only if it reaches this close to the bottom of the screen. */
const BOTTOM_BAND_PX = 140;

export type Rect = { top: number; bottom: number; left: number; right: number };

export type ChatLayout =
  | { kind: "default" }
  | { kind: "lift"; liftPx: number }
  | { kind: "hidden" };

/**
 * Where the launcher goes, from the bars on screen.
 *
 * `bars` are the visible `data-chat-avoid` elements; `launcher` is the
 * launcher's box at its default place (null when unknown: every bar counts).
 */
export function chatLayout(input: {
  viewportHeight: number;
  bars: Rect[];
  launcher: Rect | null;
}): ChatLayout {
  const { viewportHeight: vh, bars, launcher } = input;
  let lift = 0;
  for (const bar of bars) {
    if (bar.bottom <= bar.top || bar.right <= bar.left) continue;
    if (bar.top >= vh || bar.bottom < vh - BOTTOM_BAND_PX) continue;
    if (launcher && (bar.right <= launcher.left || bar.left >= launcher.right)) continue;
    lift = Math.max(lift, Math.ceil(vh - bar.top) + CHAT_GAP_PX);
  }
  if (lift <= BASE_OFFSET_PX) return { kind: "default" };
  if (lift + LAUNCHER_PX > vh / 2) return { kind: "hidden" };
  return { kind: "lift", liftPx: lift };
}

export type ChatTuckMode = "drawer" | "checkout" | null;

const CHECKOUT_PATH = /^\/[^/]+\/order\/(?!history(?:\/|$))[^/]+\/[^/]+/;

/** Where the greeting bubbles are hidden on desktop too: drawer open, checkout. */
export function chatTuckMode(
  pathname: string | null | undefined,
  drawerOpen: boolean,
): ChatTuckMode {
  if (drawerOpen) return "drawer";
  if (pathname && CHECKOUT_PATH.test(pathname)) return "checkout";
  return null;
}

const NOT_DRAGGED = ':not([style*="top:"])';

export const CHAT_WIDGET_PLACEMENT_CSS = `
html[data-chat-tuck] #chat-widget-proactive,
html[data-chat-tuck] #chat-widget-grabber {
  display: none !important;
}
@media (max-width: 600px) {
  #chat-widget-proactive,
  #chat-widget-grabber {
    display: none !important;
  }
}
html[data-chat-layout="lift"] #chat-widget-bubble${NOT_DRAGGED} {
  bottom: var(--chat-lift) !important;
}
html[data-chat-layout="lift"]:has(#chat-widget-branding) #chat-widget-bubble${NOT_DRAGGED} {
  bottom: calc(var(--chat-lift) + 40px) !important;
}
html[data-chat-layout="lift"] #chat-widget-branding${NOT_DRAGGED} {
  bottom: var(--chat-lift) !important;
}
html[data-chat-layout="lift"] #chat-widget-proactive${NOT_DRAGGED},
html[data-chat-layout="lift"] #chat-widget-grabber${NOT_DRAGGED} {
  bottom: calc(var(--chat-lift) + 74px) !important;
}
html[data-chat-layout="lift"]:has(#chat-widget-branding) #chat-widget-proactive${NOT_DRAGGED},
html[data-chat-layout="lift"]:has(#chat-widget-branding) #chat-widget-grabber${NOT_DRAGGED} {
  bottom: calc(var(--chat-lift) + 114px) !important;
}
html:has([data-chat-hide]) #chat-widget-bubble,
html:has([data-chat-hide]) #chat-widget-branding,
html:has([data-chat-hide]) #chat-widget-proactive,
html:has([data-chat-hide]) #chat-widget-grabber,
html[data-chat-layout="hidden"] #chat-widget-bubble,
html[data-chat-layout="hidden"] #chat-widget-branding,
html[data-chat-layout="hidden"] #chat-widget-proactive,
html[data-chat-layout="hidden"] #chat-widget-grabber {
  display: none !important;
}
`;

/** The attribute every sticky bottom CTA carries. */
export const CHAT_AVOID_ATTR = "data-chat-avoid";

/**
 * The visible bars in the document. A bar marked `data-chat-avoid` counts
 * only while it is actually fixed or sticky, so a button that is sticky on
 * phones and static on desktop is ignored on desktop; `"always"` counts
 * whenever it is on screen (the drawer footer, inside the fixed drawer).
 */
export function measureChatBars(doc: Document): Rect[] {
  const out: Rect[] = [];
  const view = doc.defaultView;
  doc.querySelectorAll<HTMLElement>(`[${CHAT_AVOID_ATTR}]`).forEach((el) => {
    const style = view?.getComputedStyle(el);
    if (!style || style.display === "none" || style.visibility === "hidden") return;
    const mode = el.getAttribute(CHAT_AVOID_ATTR);
    if (mode !== "always" && style.position !== "fixed" && style.position !== "sticky") return;
    const r = el.getBoundingClientRect();
    if (r.height <= 0 || r.width <= 0) return;
    out.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
  });
  return out;
}
